/**
 * Host-injected React Query client, mirroring the api-client host-injection
 * (see `configureApiClient` in ./api-client.ts). Each app root owns its own
 * `QueryClient` (via `createQueryClient`), so platform-agnostic Redux thunks in
 * `shared/store` can't hold one directly. The host registers its client here at
 * bootstrap and shared code invalidates through the generic helper below.
 *
 * The helper no-ops before a client is registered (e.g. widget-app / tests that
 * don't mount React Query), so callers never need to guard.
 *
 * Kept dependency-free on purpose: it's imported by Redux thunks that widget-app
 * bundles, so it must NOT pull the react-query runtime or the `api-client.ts`
 * chain — widget-app's webpack can't resolve `.ts`, and it never mounts React
 * Query so it shouldn't bundle it. That's why this lives here and not in the
 * (typed, `QueryClient`-instantiating) ./query-client.ts. The `@tanstack/...`
 * imports below are type-only (erased at build). Query keys shared with a thunk
 * also live here for the same reason.
 *
 * @typedef {import('@tanstack/react-query').QueryClient} QueryClient
 */

// ---------- Query keys shared across the RQ/Redux boundary ----------

/**
 * Query keys shared across the RQ/Redux boundary. A thunk that mutates server
 * state dispatches `invalidateQueryCache(queryKeys.x)`; the owning RQ hook
 * references the same entry, so there's a single source of truth. Add new
 * cross-boundary keys here.
 *
 * Each entry is cast `@type {const}` so it stays a literal tuple rather than
 * widening to `string[]` — consumers spread a key to derive a more specific one
 * (e.g. `[...queryKeys.rotationsTrainees, id]`) and need the tuple preserved.
 */
export const queryKeys = {
  /**
   * Per-org, per-field profile usage counts (`stats[fieldKey][optionCode]`),
   * backing the org-structure "used in N records" counts. Mirrored by
   * `userStatsKeys.all` in ../hooks/profile-fields.hooks.js.
   */
  userGroupStats: /** @type {const} */ (['userGroupStats']),
  /**
   * Prefix for every rotation's trainee list (`['rotations','trainees', id]`).
   * A user update (e.g. deactivation) can change who appears as a trainee in
   * any rotation, so the thunk invalidates the whole prefix rather than one id.
   * Mirrors `rotationsKeys.trainees` in ../hooks/rotations.hooks.ts.
   */
  rotationsTrainees: /** @type {const} */ (['rotations', 'trainees']),
  /**
   * Whole rotations domain. Deleting a user can affect not just trainees but
   * `team.staff`/`team.contacts` (detail), `ownerIds` (list), and timeslots, so
   * delete thunks invalidate the entire prefix. Mirrors `rotationsKeys.all`.
   */
  rotationsAll: /** @type {const} */ (['rotations']),
  /**
   * Whole knowledge-dashboard domain. Mirrors `knowledgeDashboardKeys.all` in
   * ../hooks/knowledge-dashboard.hooks.js, which derives every widget key from it.
   */
  knowledgeDashboard: /** @type {const} */ (['knowledgeDashboard']),
};

// ---------- Client registration & invalidation ----------

/** @type {QueryClient | null} */
let queryClient = null;

/**
 * Register the host app's QueryClient. Call once at bootstrap, right after
 * `createQueryClient()`.
 * @param {QueryClient} client
 */
export const registerQueryClient = client => {
  queryClient = client;
};

/**
 * Invalidate the React Query cache for a given key from outside React. Marks
 * matching queries stale so they refetch on next mount, and refetches
 * immediately any that are currently mounted. No-ops when no client is
 * registered.
 *
 * This is the low-level registry helper — the ONLY code that should call it is
 * `queryCacheMiddleware`. Redux thunks invalidate by dispatching the
 * `invalidateQueryCache` action instead, keeping the RQ↔Redux coupling in one
 * seam (see shared/store/middlewares/query-cache.middleware.js).
 * @param {import('@tanstack/react-query').QueryKey} queryKey
 */
export const invalidateQueryClientCache = queryKey => {
  queryClient?.invalidateQueries({ queryKey });
};
